import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, Inbox, Loader2 } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { cn, hashHue, initials } from '@/lib/utils';

// ── Badge ──────────────────────────────────────────────────────────────
export const badgeVariants = cva('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11.5px] font-medium leading-5 whitespace-nowrap', {
  variants: {
    tone: {
      neutral: 'border-border bg-muted text-muted-foreground',
      primary: 'border-primary/20 bg-primary/10 text-primary',
      success: 'border-success/25 bg-success/10 text-success',
      warning: 'border-warning/30 bg-warning/10 text-warning',
      danger: 'border-destructive/25 bg-destructive/10 text-destructive',
      info: 'border-info/25 bg-info/10 text-info',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

export function Badge({ className, tone, children, dot }: VariantProps<typeof badgeVariants> & { className?: string; children: ReactNode; dot?: boolean }) {
  return (
    <span className={cn(badgeVariants({ tone }), className)}>
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

const STATUS_TONES: Record<string, VariantProps<typeof badgeVariants>['tone']> = {
  active: 'success', confirmed: 'success', paid: 'success', completed: 'success', done: 'success', succeeded: 'success', approved: 'success', issued: 'success',
  published: 'success', attended: 'success', accepted: 'success', delivered: 'success', sent: 'success', ready: 'success', green: 'success', checked_in: 'info', rewarded: 'success', redeemed: 'success', occupied: 'info',
  pending: 'warning', processing: 'warning', scheduled: 'info', waiting: 'warning', offered: 'info', queued: 'warning', draft: 'neutral', requested: 'warning', open: 'info',
  past_due: 'danger', failed: 'danger', cancelled: 'neutral', canceled: 'neutral', no_show: 'danger', void: 'neutral', refunded: 'neutral', partially_refunded: 'warning', expired: 'neutral',
  suspended: 'danger', injured: 'danger', revoked: 'danger', rejected: 'danger', hidden: 'neutral', red: 'danger', amber: 'warning', resting: 'warning', retired: 'neutral', sold: 'neutral',
  paused: 'warning', skipped: 'neutral', joined: 'info', invited: 'neutral', qualified: 'info', overdue: 'danger', due_soon: 'warning', low: 'danger', closed: 'neutral', disabled: 'neutral',
};

export function StatusBadge({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge tone={STATUS_TONES[value] ?? 'neutral'} dot>
      {value.replace(/_/g, ' ')}
    </Badge>
  );
}

// ── Card ───────────────────────────────────────────────────────────────
export function Card({ className, children, ...props }: ComponentProps<'div'>) {
  return (
    <div className={cn('card-surface', className)} {...props}>
      {children}
    </div>
  );
}

export function CardHeader({ title, description, actions, className, icon }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string; icon?: ReactNode }) {
  return (
    <div className={cn('flex items-start justify-between gap-4 border-b px-5 py-4', className)}>
      <div className="flex min-w-0 items-start gap-3">
        {icon && <div className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary [&_svg]:size-4">{icon}</div>}
        <div className="min-w-0">
          <h3 className="font-semibold leading-tight">{title}</h3>
          {description && <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

// ── Avatar ─────────────────────────────────────────────────────────────
export function Avatar({ src, first, last, size = 32, className }: { src?: string | null; first?: string | null; last?: string | null; size?: number; className?: string }) {
  const hue = hashHue(`${first ?? ''}${last ?? ''}`);
  return src ? (
    <img src={src} alt="" className={cn('shrink-0 rounded-full object-cover ring-1 ring-border', className)} style={{ width: size, height: size }} />
  ) : (
    <span
      className={cn('grid shrink-0 place-items-center rounded-full font-semibold text-white', className)}
      style={{ width: size, height: size, fontSize: size * 0.38, background: `linear-gradient(135deg, hsl(${hue} 45% 52%), hsl(${(hue + 40) % 360} 45% 38%))` }}
    >
      {initials(first, last)}
    </span>
  );
}

// ── Feedback ───────────────────────────────────────────────────────────
export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-5 animate-spin text-muted-foreground', className)} />;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton h-4', className)} />;
}

export function EmptyState({ title, description, icon, action, className }: { title: ReactNode; description?: ReactNode; icon?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      <div className="mb-3 grid size-12 place-items-center rounded-2xl bg-muted text-muted-foreground [&_svg]:size-6">{icon ?? <Inbox />}</div>
      <p className="font-medium">{title}</p>
      {description && <p className="mt-1 max-w-sm text-[13px] text-muted-foreground">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border bg-muted px-1.5 py-0.5 font-sans text-[11px] text-muted-foreground">{children}</kbd>;
}

// ── Switch / checkbox ──────────────────────────────────────────────────
export function Switch({ className, ...props }: ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      className={cn('peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent bg-input transition-colors data-[state=checked]:bg-primary disabled:cursor-not-allowed disabled:opacity-50', className)}
      {...props}
    >
      <SwitchPrimitive.Thumb className="pointer-events-none block size-4 rounded-full bg-white shadow-sm transition-transform data-[state=checked]:translate-x-4 rtl:data-[state=checked]:-translate-x-4" />
    </SwitchPrimitive.Root>
  );
}

export function Checkbox({ className, ...props }: ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root className={cn('grid size-4 shrink-0 place-items-center rounded border border-input bg-surface data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground', className)} {...props}>
      <CheckboxPrimitive.Indicator>
        <Check className="size-3" strokeWidth={3} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

// ── Tabs ───────────────────────────────────────────────────────────────
export const Tabs = TabsPrimitive.Root;
export function TabsList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
  return <TabsPrimitive.List className={cn('inline-flex items-center gap-1 overflow-x-auto border-b', className)} {...props} />;
}
export function TabsTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn('-mb-px whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-foreground', className)}
      {...props}
    />
  );
}
export function TabsContent({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content className={cn('pt-5 focus-visible:ring-0', className)} {...props} />;
}

// ── Tooltip ────────────────────────────────────────────────────────────
export function Tooltip({ content, children, side = 'top' }: { content: ReactNode; children: ReactNode; side?: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <TooltipPrimitive.Root delayDuration={250}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content side={side} sideOffset={6} className="z-[60] max-w-xs animate-fade-in rounded-md bg-foreground px-2 py-1 text-xs text-background shadow-lift">
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
export const TooltipProvider = TooltipPrimitive.Provider;
